import { Cesium } from 'app/cesium';
import { MapService } from 'app/map/services/map.service';
import { MapTooltipService } from 'app/map/services/map_tooltip.service';
import { EventRegistry } from 'app/shared/events/event_registry';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { cartesian3ToDegrees } from 'app/shared/utilities/mapping_utils';
import { InvalidPolygonDetail } from 'app/shared/zones/zone_history/zone_history_editor_fixer_utils';
import * as _ from 'lodash';
import DebugService from '../../shared/debug.service';
import {
    ScreenSpaceClickEvent,
    ScreenSpaceMovementEvent,
} from 'app/context_menu/asset_mover/AssetMoverMapInputHandler';
import { MapInputHandlerService } from 'app/map/services/map_input_handling/MapInputHandlerService';
import {
    AbstractMapInputHandler,
    IMapInputHandler,
} from 'app/map/services/map_input_handling/MapInputHandler';

const log = (...args) => DebugService.log('MapDrawingInputHandler', ...args);

const HANDLE = Symbol('handle');
const HALF_HANDLE = Symbol('half handle');
const PREVIEW_POLY = Symbol('preview polygon');

const POINT_OF_CONCERN_ID = 'point-of-concern';

const MIN_POLY_POINTS = 3;

interface ShapeToSave {
    positions: Array<Cesium.Cartesian3>;
}

export type SaveFunction = (data: ShapeToSave) => Promise<{
    success: boolean;
    msg: string;
    invalidPolygonDetail: InvalidPolygonDetail;
}>;

interface EditableShape {
    entityCollection: Cesium.CustomDataSource;
    handles: Array<any>;
    halfHandles: Array<any>;
    previewPolygon: any;
}

export class MapDrawingInputHandler extends AbstractMapInputHandler implements IMapInputHandler {
    _mapInputHandlerService: MapInputHandlerService;
    _clockHandler;
    _mapTooltipService;
    _selectionService;
    _mapService;
    _handlerAvailabilityEventRegistry: EventRegistry;
    _handlerStateEventRegistry: EventRegistry;
    _shape: EditableShape;
    _finishedDrawing;
    _dragging;
    _saveFn: SaveFunction;
    _lockedClock;
    _onStopDrawing;

    constructor(
        mapInputHandlerService: MapInputHandlerService,
        clockHandler: ClockHandler,
        mapTooltipService: MapTooltipService,
        selectionService: SelectionService,
        mapService: MapService,
    ) {
        super();
        this._mapInputHandlerService = mapInputHandlerService;
        this._clockHandler = clockHandler;
        this._mapTooltipService = mapTooltipService;
        this._selectionService = selectionService;
        this._mapService = mapService;

        this._handlerAvailabilityEventRegistry = new EventRegistry();
        this._handlerStateEventRegistry = new EventRegistry();

        this._mapInputHandlerService.onActiveHandlerChange(() => {
            this._handlerAvailabilityEventRegistry.notifyListeners(
                !this._mapInputHandlerService.isDefaultMode,
            );
        });
    }

    static get entityTypes() {
        return {
            HANDLE,
            HALF_HANDLE,
            PREVIEW_POLY,
        };
    }

    cartesian3FromMousePos(mousePos) {
        // option 1: doesn't work with 3d terrain
        // return MapService.viewer.scene.camera.pickEllipsoid(mousePos);

        // option 2: works with 3d terrain
        const viewer = this._mapService.viewer;
        const cartesian2 = new Cesium.Cartesian2(mousePos.x, mousePos.y);
        const pickRay = viewer.scene.camera.getPickRay(cartesian2);
        return viewer.scene.globe.pick(pickRay, viewer.scene);
    }

    getTargetDetailsAtMousePos(mousePos) {
        const firstValidTarget = _.find(
            this._mapService.viewer.scene.drillPick(mousePos),
            (pick) => {
                return _.includes(
                    _.values(MapDrawingInputHandler.entityTypes),
                    _.get(pick, 'id.entityType'),
                );
            },
        );
        return {
            id: _.get(firstValidTarget, 'id.id'),
            entityType: _.get(firstValidTarget, 'id.entityType'),
        };
    }

    static newHandleEntitySpec(cartesian3Pos) {
        return {
            position: cartesian3Pos,
            billboard: {
                image: '/assets/icons/measuring_point.svg',
                width: 18,
                height: 18,
                // for 3d terrain, may be the cause of a page hang/freeze
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: 0,
            },
            entityType: MapDrawingInputHandler.entityTypes.HANDLE,
        };
    }

    static newHalfHandleEntitySpec(cartesian3Pos) {
        return {
            position: cartesian3Pos,
            billboard: {
                image: '/assets/icons/measuring_point.svg',
                color: Cesium.Color.WHITE.withAlpha(0.3),
                width: 15,
                height: 15,
                // for 3d terrain, may be the cause of a page hang/freeze
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: 0,
            },
            entityType: MapDrawingInputHandler.entityTypes.HALF_HANDLE,
        };
    }

    static newPreviewPolygonEntitySpec(getPositions) {
        return {
            polygon: {
                hierarchy: new Cesium.CallbackProperty(getPositions, false),
                material: Cesium.Color.WHITE.withAlpha(0.5),
            },
            entityType: MapDrawingInputHandler.entityTypes.PREVIEW_POLY,
        };
    }

    static newEditableShape(): EditableShape {
        return {
            entityCollection: new Cesium.CustomDataSource(),
            handles: [],
            halfHandles: [],
            previewPolygon: null,
        };
    }

    static getShapeHandleCartesian3s(shape) {
        const positions = _.map(shape.handles, (handle) =>
            handle.position ? handle.position.getValue(Cesium.Iso8601.MINIMUM_VALUE) : null,
        );
        return { positions: positions };
    }

    addHighEyeOffsetToHandle(handle) {
        handle.billboard.eyeOffset = new Cesium.CallbackProperty(() => {
            let ZEyeOffset = -15;
            const position = _.get(handle, 'position');
            const camera = _.get(this._mapService, 'viewer.camera');
            if (position && camera) {
                let distance = Cesium.Cartesian3.distance(
                    position.getValue(Cesium.Iso8601.MINIMUM_VALUE),
                    camera.position,
                );
                if (distance > 1000000) {
                    distance = 1;
                }
                ZEyeOffset = -distance / 9;
            }
            if (handle.id === POINT_OF_CONCERN_ID) {
                ZEyeOffset = ZEyeOffset - 1;
            }
            return new Cesium.Cartesian3(0, 0, ZEyeOffset);
        }, false);
    }

    addNewHandleToShape(shape, cartesian3Pos, index?) {
        const spec = MapDrawingInputHandler.newHandleEntitySpec(cartesian3Pos);
        const entity = shape.entityCollection.entities.add(spec);
        this.addHighEyeOffsetToHandle(entity);
        shape.handles.splice(index ? index : shape.handles.length - 1, 0, entity);
    }

    static removeHandleAtIndexFromShape(shape, index) {
        const handle = shape.handles[index];
        shape.entityCollection.entities.remove(handle);
        shape.handles.splice(index, 1);
    }

    static setShowShapeHandles(shape, boolean) {
        _.forEach(shape.handles, (handle) => {
            handle.show = boolean;
        });
        _.forEach(shape.halfHandles, (halfHandle) => {
            halfHandle.show = boolean;
        });
    }

    refreshShapeHalfHandles(shape) {
        const handleCartesian3s = MapDrawingInputHandler.getShapeHandleCartesian3s(shape);

        function calculateNewHalfMarkerCartesian3(handleIndex) {
            const fromCartographic = new Cesium.Cartographic.fromCartesian(
                handleCartesian3s.positions[handleIndex],
            );
            const toCartesian3 =
                handleCartesian3s.positions[
                    handleIndex < shape.handles.length - 1 ? handleIndex + 1 : 0
                ];
            const toCartographic = new Cesium.Cartographic.fromCartesian(toCartesian3);
            const geodesic = new Cesium.EllipsoidGeodesic(fromCartographic, toCartographic);
            return Cesium.Ellipsoid.WGS84.cartographicToCartesian(
                geodesic.interpolateUsingFraction(0.5),
            );
        }

        const newHalfMarkerCartesian3s = _.range(handleCartesian3s.positions.length).map((i) =>
            calculateNewHalfMarkerCartesian3(i),
        );

        _.forEach(shape.halfHandles, () => {
            shape.entityCollection.entities.remove(shape.halfHandles.pop());
        });

        _.forEach(newHalfMarkerCartesian3s, (cartesian3) => {
            const spec = MapDrawingInputHandler.newHalfHandleEntitySpec(cartesian3);
            const entity = shape.entityCollection.entities.add(spec);
            this.addHighEyeOffsetToHandle(entity);
            shape.halfHandles.push(entity);
        });
    }

    static moveShape(shape, fromCartesianPos, toCartesianPos) {
        const diff = new Cesium.Cartesian3();
        Cesium.Cartesian3.subtract(toCartesianPos, fromCartesianPos, diff);

        _.forEach(shape.handles, (handle) => {
            const cartesian3 = Cesium.Cartesian3.add(
                diff,
                handle.position.getValue(Cesium.Iso8601.MINIMUM_VALUE),
                new Cesium.Cartesian3(),
            );
            const { longitude, latitude } = cartesian3ToDegrees(cartesian3);
            handle.position.setValue(new Cesium.Cartesian3.fromDegrees(longitude, latitude, 0));
        });
    }

    destroyShape(shape) {
        shape.entityCollection.entities.removeAll();
        this._mapService.removeDataSource(shape.entityCollection);
    }

    public onChange(callback: () => void): () => void {
        return this._handlerStateEventRegistry.addEventListener(callback);
    }

    public mouseMove(event: ScreenSpaceMovementEvent): void {
        const mouseXY = event.endPosition;
        let tooltip = '';

        if (!this._shape) {
            tooltip = '<b>Left Click</b> to add the first point';
        } else {
            if (!this._finishedDrawing && this._shape.handles.length > 0) {
                const cartesian3 = this.cartesian3FromMousePos(mouseXY);
                if (cartesian3) {
                    _.last(this._shape.handles).position.setValue(cartesian3);
                }
                if (this._shape.handles.length > MIN_POLY_POINTS) {
                    tooltip = '<b>Left Click</b> to add another point, <b>Right Click</b> to stop';
                } else {
                    tooltip =
                        '<b>Left Click</b> to add another point, <b>Right Click</b> to cancel';
                }
            } else if (this._dragging) {
                const cartesian3 = this.cartesian3FromMousePos(mouseXY);

                if (
                    cartesian3 &&
                    this._dragging.entityType === MapDrawingInputHandler.entityTypes.PREVIEW_POLY
                ) {
                    MapDrawingInputHandler.moveShape(
                        this._shape,
                        this._dragging.lastCartesian3Pos,
                        cartesian3,
                    );
                    this._dragging.lastCartesian3Pos = cartesian3;
                } else if (
                    cartesian3 &&
                    this._dragging.entityType === MapDrawingInputHandler.entityTypes.HANDLE
                ) {
                    this._shape.handles[this._dragging.handleIndex].position.setValue(cartesian3);
                    this.refreshShapeHalfHandles(this._shape);
                }
            } else {
                const target = this.getTargetDetailsAtMousePos(mouseXY);

                if (target.entityType === MapDrawingInputHandler.entityTypes.PREVIEW_POLY) {
                    tooltip = '<b>Drag</b> to move shape';
                } else if (target.entityType === MapDrawingInputHandler.entityTypes.HANDLE) {
                    if (this._shape.handles.length === MIN_POLY_POINTS) {
                        tooltip = '<b>Drag</b> to move point';
                    } else {
                        tooltip = '<b>Drag</b> to move point, or <b>Right Click</b> to remove';
                    }
                } else if (target.entityType === MapDrawingInputHandler.entityTypes.HALF_HANDLE) {
                    tooltip = '<b>Drag</b> to create another point';
                } else if (this._shape.handles.length < MIN_POLY_POINTS) {
                    tooltip = '<b>Left Click</b> to add another point';
                }
            }
        }
        if (tooltip) {
            this._mapTooltipService.setTooltip(tooltip);
        } else {
            this._mapTooltipService.setActive(false);
        }
    }

    public leftDown(event: ScreenSpaceClickEvent): void {
        const mouseXY = event.position;

        if (this._shape) {
            const target = this.getTargetDetailsAtMousePos(mouseXY);

            if (target.entityType === MapDrawingInputHandler.entityTypes.PREVIEW_POLY) {
                this.clearOutPointOfConcern();
                this._mapService.disableDragging();
                this._dragging = {
                    entityType: MapDrawingInputHandler.entityTypes.PREVIEW_POLY,
                    lastCartesian3Pos: this.cartesian3FromMousePos(mouseXY),
                };
                MapDrawingInputHandler.setShowShapeHandles(this._shape, false);
            } else if (target.entityType === MapDrawingInputHandler.entityTypes.HANDLE) {
                this.clearOutPointOfConcern();
                this._mapService.disableDragging();
                this._dragging = {
                    entityType: MapDrawingInputHandler.entityTypes.HANDLE,
                    handleIndex: _.findIndex(
                        this._shape.handles,
                        (handle) => handle.id === target.id,
                    ),
                };
            } else if (target.entityType === MapDrawingInputHandler.entityTypes.HALF_HANDLE) {
                this.clearOutPointOfConcern();
                this._mapService.disableDragging();
                const cartesian3 = this.cartesian3FromMousePos(mouseXY);
                const halfHandleIndex = _.findIndex(
                    this._shape.halfHandles,
                    (halfHandle) => halfHandle.id === target.id,
                );
                this.addNewHandleToShape(this._shape, cartesian3, halfHandleIndex + 1);
                this.refreshShapeHalfHandles(this._shape);
                this._dragging = {
                    entityType: MapDrawingInputHandler.entityTypes.HANDLE,
                    handleIndex: halfHandleIndex + 1,
                };
            }
        }
    }

    public leftUp(): void {
        if (this._dragging) {
            this.clearOutPointOfConcern();
            if (this._dragging.entityType === MapDrawingInputHandler.entityTypes.PREVIEW_POLY) {
                this.refreshShapeHalfHandles(this._shape);
            }
            MapDrawingInputHandler.setShowShapeHandles(this._shape, true);
            this._mapService.enableDragging();
            this._dragging = null;
        }
    }

    public leftClick(event: ScreenSpaceClickEvent): void {
        const cartesian3 = this.cartesian3FromMousePos(event.position);

        if (!this._shape) {
            this.clearOutPointOfConcern();
            this._shape = MapDrawingInputHandler.newEditableShape();
            this._mapService.addDataSource(this._shape.entityCollection);
            this.addNewHandleToShape(this._shape, cartesian3);
            this.addNewHandleToShape(this._shape, cartesian3);
        } else if (!this._finishedDrawing) {
            this.clearOutPointOfConcern();
            if (!this.pointDuplicated()) {
                this.addNewHandleToShape(this._shape, cartesian3);
            }
            if (!this._shape.previewPolygon && _.size(this._shape.handles) === MIN_POLY_POINTS) {
                const spec = MapDrawingInputHandler.newPreviewPolygonEntitySpec(() => {
                    return MapDrawingInputHandler.getShapeHandleCartesian3s(this._shape);
                });
                this._shape.previewPolygon = this._shape.entityCollection.entities.add(spec as any);
            }
        }
    }

    public async rightClick(event: ScreenSpaceClickEvent): Promise<void> {
        const mouseXY = event.position;
        const target = this.getTargetDetailsAtMousePos(mouseXY);

        if (!this._finishedDrawing && this._shape) {
            this.clearOutPointOfConcern();
            if (this._shape.handles.length > MIN_POLY_POINTS) {
                MapDrawingInputHandler.removeHandleAtIndexFromShape(
                    this._shape,
                    this._shape.handles.length - 1,
                );
                this.refreshShapeHalfHandles(this._shape);
                this._finishedDrawing = true;
                await this._handlerStateEventRegistry.notifyListeners();
            } else {
                this.destroyShape(this._shape);
                this._shape = null;
                await this._handlerStateEventRegistry.notifyListeners();
            }
        } else if (
            target.entityType === MapDrawingInputHandler.entityTypes.HANDLE &&
            this._shape.handles.length > MIN_POLY_POINTS
        ) {
            this.clearOutPointOfConcern();
            const handleIndex = _.findIndex(
                this._shape.handles,
                (handle) => handle.id === target.id,
            );
            MapDrawingInputHandler.removeHandleAtIndexFromShape(this._shape, handleIndex);
            this.refreshShapeHalfHandles(this._shape);
        }
    }

    drawnValidShape() {
        return (
            this._shape && this._finishedDrawing && this._shape.handles.length >= MIN_POLY_POINTS
        );
    }

    attemptSave() {
        if (this.drawnValidShape()) {
            return this._saveFn(
                MapDrawingInputHandler.getShapeHandleCartesian3s(this._shape) as ShapeToSave,
            );
        }
    }

    pointDuplicated() {
        const pos = MapDrawingInputHandler.getShapeHandleCartesian3s(this._shape).positions;
        const totalPos = pos.length;
        return pos[totalPos - 1].equals(pos[totalPos - 2]);
    }

    /**
     * You're allowed 1 red dot on the map to indicate a point of concern.
     *  It will disappear as soon as anything is touched.
     */
    public setPointOfConcern(position: Cesium.Cartesian3): void {
        let entity = this._shape.entityCollection.entities.getById(POINT_OF_CONCERN_ID);
        if (!entity) {
            const spec = new Cesium.Entity({
                billboard: {
                    image: '/assets/icons/measuring_point.svg',
                    color: Cesium.Color.RED.withAlpha(1),
                    width: 20,
                    height: 20,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                    disableDepthTestDistance: 0,
                },
                position: new Cesium.ConstantPositionProperty(position),
                id: POINT_OF_CONCERN_ID,
            } as any);
            entity = this._shape.entityCollection.entities.add(spec);
            this.addHighEyeOffsetToHandle(entity);
        }
        (entity.position as Cesium.ConstantPositionProperty).setValue(position);
    }

    private clearOutPointOfConcern() {
        if (this._shape?.entityCollection) {
            this._shape.entityCollection.entities.removeById(POINT_OF_CONCERN_ID);
        }
    }

    async startDrawing(
        cartesian3Points: Array<Cesium.Cartesian3>,
        lockClock: boolean,
        saveFn: SaveFunction,
    ) {
        this._finishedDrawing = false;
        this._shape = null;
        this._dragging = null;
        this._lockedClock = lockClock;
        this._saveFn = saveFn;

        if (cartesian3Points) {
            this._shape = MapDrawingInputHandler.newEditableShape();
            this._finishedDrawing = true;
            this._mapService.addDataSource(this._shape.entityCollection);
            _.forEach(cartesian3Points, (cartesian3) => {
                this.addNewHandleToShape(this._shape, cartesian3);
            });
            const previewPolySpec = MapDrawingInputHandler.newPreviewPolygonEntitySpec(() => {
                return MapDrawingInputHandler.getShapeHandleCartesian3s(this._shape);
            });
            this._shape.previewPolygon = this._shape.entityCollection.entities.add(
                previewPolySpec as any,
            );
            this.refreshShapeHalfHandles(this._shape);
        }

        if (lockClock) {
            this._clockHandler.setLocked(true);
        }

        this._mapInputHandlerService.setActiveHandler(this);
        await this._selectionService.setFrozen(true);

        log('Started drawing', this);
        await this._handlerStateEventRegistry.notifyListeners();

        return new Promise((resolve) => (this._onStopDrawing = resolve));
    }

    public cleanUp(wasCancelled: boolean = true): void {
        this._selectionService.setFrozen(false);
        this._mapTooltipService.setActive(false);

        if (this._lockedClock) {
            this._clockHandler.setLocked(false);
        }

        if (this._shape) {
            this.destroyShape(this._shape);
        }

        log('Stopped drawing', this);

        this._onStopDrawing(wasCancelled);
    }
}
